/**
 * Seletor de arquivos para upload antes de submeter comentário.
 * Mostra mini-lista com X para remover. Limite: 5 arquivos, 5MB cada.
 */
import { Paperclip, X } from 'lucide-react';
import { useRef } from 'react';

const MAX_FILES = 5;
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function TaskCommentAttachmentPicker({ files, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files ?? []);
    const filtered = selected.filter((f) => f.size <= MAX_SIZE);
    const combined = [...files, ...filtered].slice(0, MAX_FILES);
    onChange(combined);
    // reset input para permitir re-seleção do mesmo arquivo
    if (inputRef.current) inputRef.current.value = '';
  }

  function removeFile(idx: number) {
    onChange(files.filter((_, i) => i !== idx));
  }

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={files.length >= MAX_FILES}
        className="flex items-center gap-1 rounded px-2 py-1 text-[11px] border border-[var(--border)] text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] disabled:opacity-40 disabled:cursor-not-allowed transition"
        title={files.length >= MAX_FILES ? `Máximo de ${MAX_FILES} arquivos` : 'Anexar arquivo'}
      >
        <Paperclip className="h-3 w-3" />
        Anexo{files.length > 0 ? ` (${files.length})` : ''}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          {files.map((f, i) => (
            <div
              key={`${f.name}-${f.size}-${i}`}
              className="flex items-center gap-1.5 rounded border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] px-2 py-1 text-xs text-[var(--fg)]"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-[var(--fg-muted)]" />
              <span className="truncate max-w-[140px]">{f.name}</span>
              <span className="text-[var(--fg-muted)] shrink-0">({formatBytes(f.size)})</span>
              {f.size > MAX_SIZE && (
                <span className="text-red-500 text-[10px] shrink-0">muito grande</span>
              )}
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="ml-auto text-[var(--fg-muted)] hover:text-red-600 transition"
                title="Remover"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
